import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  styled,
  Typography,
  useTheme,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';

import type {
  StandingOrderContext,
  StandingOrderSummaryInputs,
} from '../machines/standingOrder.machine';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';
import { formatStandingOrderPickupDateTimesCsv } from '../utils/formatStandingOrderPickupDateTimesCsv';
import { TripLegType } from '@/data/models/reservation.model';
import { usePortalContext } from '@/contexts/PortalProvider';

const PREFIX = 'StandingOrderForm';

const classes = {
  container: `${PREFIX}-container`,
  standingOrderDescription: `${PREFIX}-standingOrderDescription`,
  standingOrderRange: `${PREFIX}-standingOrderRange`,
  standingOrderDate: `${PREFIX}-standingOrderDate`,
  standingOrderGrid: `${PREFIX}-standingOrderGrid`,
  standingOrderSummary: `${PREFIX}-standingOrderSummary`,
};

const standingOrderDayConfigs = [
  { key: 'monday', labelKey: 'reservation.monday' },
  { key: 'tuesday', labelKey: 'reservation.tuesday' },
  { key: 'wednesday', labelKey: 'reservation.wednesday' },
  { key: 'thursday', labelKey: 'reservation.thursday' },
  { key: 'friday', labelKey: 'reservation.friday' },
  { key: 'saturday', labelKey: 'reservation.saturday' },
  { key: 'sunday', labelKey: 'reservation.sunday' },
] as const;

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  [`& .${classes.standingOrderDescription}`]: {
    color: theme.palette.lightTextSecondary.main,
  },
  [`& .${classes.standingOrderRange}`]: {
    marginTop: theme.spacing(1),
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    maxWidth: 740,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  [`& .${classes.standingOrderDate}`]: {
    width: '100%',
  },
  [`& .${classes.standingOrderGrid}`]: {
    maxWidth: 740,
    marginTop: theme.spacing(2),
  },
  [`& .${classes.standingOrderSummary}`]: {
    marginTop: theme.spacing(2),
    color: theme.palette.lightTextSecondary.main,
  },
}));

interface StandingOrderFormProps {
  index: number;
}

export function StandingOrderForm({ index }: StandingOrderFormProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { isPortal } = usePortalContext();
  const { control, setValue } = useFormContext<any>();
  const [standingOrderRangeError, setStandingOrderRangeError] = useState('');
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs.standingOrder;
  const standingOrderSnapshot = useFieldActorSnapshot(actor);

  const fieldName = `trips.${index}.standingOrder` as const;
  const standingOrderRaw = useWatch({
    control: control as any,
    name: fieldName as any,
  });
  const standingOrderValue = !!standingOrderRaw;

  const tripType = useWatch({
    control: control as any,
    name: 'tripType' as any,
  }) as unknown as TripLegType | undefined;

  const appointmentDateTime = useWatch({
    control: control as any,
    name: `trips.${index}.appointmentDateTime` as any,
  });

  const appointmentDate = useWatch({
    control: control as any,
    name: `trips.${index}.appointmentDate` as any,
  }) as unknown as string | DateTime | undefined;

  const pickupDateTime = useWatch({
    control: control as any,
    name: `trips.${index}.pickupDateTime` as any,
  });

  const standingOrderStartDate = useWatch({
    control: control as any,
    name: `trips.${index}.standingOrderStartDate` as any,
  }) as unknown as DateTime | null | undefined;

  const standingOrderEndDate = useWatch({
    control: control as any,
    name: `trips.${index}.standingOrderEndDate` as any,
  }) as unknown as DateTime | null | undefined;

  const enabledValues = useWatch({
    control: control as any,
    name: standingOrderDayConfigs.map(
      (day) => `trips.${index}.standingOrderDays.${day.key}.enabled` as any,
    ) as any,
  }) as boolean[] | undefined;

  const pickupTimesPerDay = useWatch({
    control: control as any,
    name: standingOrderDayConfigs.map(
      (day) => `trips.${index}.standingOrderDays.${day.key}.pickupTime` as any,
    ) as any,
  }) as unknown[] | undefined;

  const enabledDayFlagsKey =
    enabledValues == null
      ? ''
      : standingOrderDayConfigs
          .map((_, i) => (enabledValues[i] ? '1' : '0'))
          .join('');

  const roundTripWillCall = useWatch({
    control: control as any,
    name: `trips.${index}.roundTripWillCall` as any,
  }) as unknown as boolean | undefined;

  const isWillCall = useWatch({
    control: control as any,
    name: `trips.${index}.isWillCall` as any,
  }) as unknown as boolean | undefined;

  const roundTripReturnDateTime = useWatch({
    control: control as any,
    name: `trips.${index}.roundTripReturnDateTime` as any,
  }) as unknown as DateTime | null | undefined;

  const previousEnabledValuesRef = useRef<boolean[]>([]);

  const standingOrderCtx =
    standingOrderSnapshot.context as StandingOrderContext;
  const shouldDisplayStandingOrderSummary =
    standingOrderCtx.shouldDisplaySummary[index] ?? false;
  const standingOrderPickupDateTimesCsv =
    standingOrderCtx.pickupDateTimesCsv[index] ?? '';
  const formattedStandingOrderPickupDateTimesCsv =
    formatStandingOrderPickupDateTimesCsv(standingOrderPickupDateTimesCsv, t);
  const standingOrderReservationCount =
    standingOrderCtx.numberOfReservations[index] ?? 0;

  useEffect(() => {
    const inputs: StandingOrderSummaryInputs = {
      appointmentDate,
      appointmentDateTime,
      pickupDateTime,
      roundTripReturnDateTime,
      roundTripWillCall,
      standingOrderStartDate,
      standingOrderEndDate,
      enabledValues,
      pickupTimesPerDay,
    };
    actor.send({ type: 'SUMMARY_INPUTS', index, inputs });
  }, [
    appointmentDate,
    appointmentDateTime,
    pickupDateTime,
    roundTripReturnDateTime,
    roundTripWillCall,
    standingOrderStartDate,
    standingOrderEndDate,
    enabledValues,
    pickupTimesPerDay,
  ]);

  useEffect(() => {
    const snapshot = actor.getSnapshot().context as StandingOrderContext;
    if (snapshot.values[index] !== standingOrderValue) {
      actor.send({ type: 'CHANGE', index, value: standingOrderValue });
    }
  }, [actor, index, standingOrderValue]);

  useEffect(() => {
    const nextStartDate =
      typeof appointmentDate === 'string' && appointmentDate.trim()
        ? DateTime.fromISO(appointmentDate)
        : appointmentDate instanceof DateTime && appointmentDate.isValid
          ? appointmentDate
          : appointmentDateTime?.isValid
            ? appointmentDateTime
            : null;

    if (nextStartDate?.isValid) {
      setValue(`trips.${index}.standingOrderStartDate` as any, nextStartDate, {
        shouldDirty: false,
        shouldValidate: true,
      });
      return;
    }

    setValue(`trips.${index}.standingOrderStartDate` as any, null, {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [appointmentDate, appointmentDateTime, index, setValue]);

  useEffect(() => {
    let message = '';

    if (standingOrderEndDate?.isValid && standingOrderStartDate?.isValid) {
      const daysDiff = standingOrderEndDate.diff(
        standingOrderStartDate,
        'days',
      ).days;
      if (daysDiff <= 0) {
        message = t('reservation.endDateMustBeAfterStartDate');
      } else if (daysDiff > 90) {
        message = t('reservation.max90');
      }
    }

    setStandingOrderRangeError(message);
  }, [standingOrderEndDate, standingOrderStartDate, t]);

  useEffect(() => {
    const previousEnabledValues = previousEnabledValuesRef.current;

    standingOrderDayConfigs.forEach((day, dayIndex) => {
      const enabled = !!enabledValues?.[dayIndex];
      const wasEnabled = !!previousEnabledValues?.[dayIndex];
      const baseField = `trips.${index}.standingOrderDays.${day.key}` as const;

      if (!enabled && wasEnabled) {
        setValue(`${baseField}.appointmentTime` as any, null, {
          shouldDirty: false,
          shouldValidate: true,
        });
        setValue(`${baseField}.pickupTime` as any, null, {
          shouldDirty: false,
          shouldValidate: true,
        });
      }

      if (enabled && !wasEnabled) {
        if (appointmentDateTime) {
          setValue(`${baseField}.appointmentTime` as any, appointmentDateTime, {
            shouldDirty: false,
            shouldValidate: true,
          });
        }
        if (pickupDateTime) {
          setValue(`${baseField}.pickupTime` as any, pickupDateTime, {
            shouldDirty: false,
            shouldValidate: true,
          });
        }
      }
    });

    previousEnabledValuesRef.current = standingOrderDayConfigs.map(
      (_, dayIndex) => !!enabledValues?.[dayIndex],
    );
  }, [
    appointmentDateTime,
    enabledDayFlagsKey,
    index,
    pickupDateTime,
    setValue,
  ]);

  // The per-day pickers are disabled, so per-day values are never overridden
  // mirror the trip's appointment/pickup so any change propagates into the enabled days.
  useEffect(() => {
    if (!appointmentDateTime) return;

    standingOrderDayConfigs.forEach((day, dayIndex) => {
      if (!enabledValues?.[dayIndex]) return;
      const fieldPath =
        `trips.${index}.standingOrderDays.${day.key}.appointmentTime` as const;

      setValue(fieldPath as any, appointmentDateTime, {
        shouldDirty: false,
        shouldValidate: true,
      });
    });
  }, [appointmentDateTime, enabledDayFlagsKey, index, setValue]);

  useEffect(() => {
    standingOrderDayConfigs.forEach((day, dayIndex) => {
      if (!enabledValues?.[dayIndex]) return;
      const fieldPath =
        `trips.${index}.standingOrderDays.${day.key}.pickupTime` as const;

      // Will-call pickups have no scheduled time; otherwise follow the trip's
      // pickup (which repopulates asynchronously after will-call is turned off).
      setValue(fieldPath as any, isWillCall ? null : (pickupDateTime ?? null), {
        shouldDirty: false,
        shouldValidate: true,
      });
    });
  }, [enabledDayFlagsKey, index, pickupDateTime, isWillCall, setValue]);

  return (
    <StyledBox className={classes.container}>
      <FormControl component="fieldset" variant="standard">
        <FormControlLabel
          control={
            <Controller
              name={fieldName as any}
              control={control}
              render={({ field }) => (
                <Checkbox
                  color="primary"
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  data-fs-element="StandingOrderReservationButton"
                  sx={{
                    '&.Mui-checked': { color: theme.palette.primary.main },
                  }}
                />
              )}
            />
          }
          label={t('reservation.standingOrder')}
        />
        {standingOrderValue && (
          <Typography
            variant="body2"
            className={classes.standingOrderDescription}>
            {t('reservation.standingOrderDescription')}
          </Typography>
        )}

        {standingOrderValue && (
          <Box className={classes.standingOrderRange}>
            <Controller
              name={`trips.${index}.standingOrderStartDate` as any}
              control={control}
              render={({ field }) => (
                <DatePicker
                  label={t('reservation.starts')}
                  value={field.value ?? null}
                  onChange={(value) => field.onChange(value)}
                  disabled
                  slotProps={{
                    textField: {
                      className: classes.standingOrderDate,
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
              )}
            />
            <Box className={classes.standingOrderDate}>
              <Controller
                name={`trips.${index}.standingOrderEndDate` as any}
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label={t('reservation.ends')}
                    value={field.value ?? null}
                    onChange={(value) => field.onChange(value)}
                    minDate={standingOrderStartDate?.plus?.({ days: 1 })}
                    maxDate={standingOrderStartDate?.plus?.({ days: 90 })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />
                )}
              />
              <FormHelperText error>{standingOrderRangeError}</FormHelperText>
            </Box>
          </Box>
        )}

        {standingOrderValue && (
          <Grid
            container
            columnSpacing={2}
            rowSpacing={1}
            alignItems="flex-start"
            className={classes.standingOrderGrid}>
            <Grid item xs={4}>
              {t('reservation.days')}
            </Grid>
            <Grid item xs={4}>
              {t('reservation.appointment')}
            </Grid>
            <Grid item xs={4}>
              {t('reservation.pickup')}
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            {standingOrderDayConfigs.map((day, dayIndex) => {
              const baseField =
                `trips.${index}.standingOrderDays.${day.key}` as const;
              const enabled = !!enabledValues?.[dayIndex];

              return (
                <Grid
                  item
                  container
                  xs={12}
                  key={day.key}
                  columnSpacing={2}
                  sx={{
                    alignItems: 'center',
                  }}>
                  <Grid item xs={4}>
                    <FormControlLabel
                      control={
                        <Controller
                          name={`${baseField}.enabled` as any}
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              color="primary"
                              checked={!!field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              onBlur={field.onBlur}
                              inputRef={field.ref}
                              sx={{
                                '&.Mui-checked': {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          )}
                        />
                      }
                      label={t(day.labelKey)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    {enabled && (
                      <Controller
                        name={`${baseField}.appointmentTime` as any}
                        control={control}
                        render={({ field }) => (
                          <DesktopTimePicker
                            label={t(
                              !isWillCall
                                ? 'reservation.appointment'
                                : 'reservation.willCallTime',
                            )}
                            value={!isWillCall ? (field.value ?? null) : null}
                            onChange={(value) => field.onChange(value)}
                            disabled
                            slotProps={{
                              textField: {
                                variant: 'outlined',
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    {enabled && (
                      <Controller
                        name={`${baseField}.pickupTime` as any}
                        control={control}
                        render={({ field }) => (
                          <DesktopTimePicker
                            label={t(
                              !isWillCall
                                ? 'reservation.pickup'
                                : 'reservation.willCallTime',
                            )}
                            value={!isWillCall ? (field.value ?? null) : null}
                            onChange={(value) => field.onChange(value)}
                            disabled
                            slotProps={{
                              textField: {
                                variant: 'outlined',
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      />
                    )}
                  </Grid>
                </Grid>
              );
            })}
            {shouldDisplayStandingOrderSummary && (
              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  className={classes.standingOrderSummary}>
                  <Trans
                    i18nKey="reservation.standingOrderFullSummary"
                    values={{
                      days: formattedStandingOrderPickupDateTimesCsv,
                      startDate: standingOrderStartDate?.toLocaleString(
                        DateTime.DATE_SHORT,
                      ),
                      endDate: standingOrderEndDate?.toLocaleString(
                        DateTime.DATE_SHORT,
                      ),
                      numberOfReservations: standingOrderReservationCount,
                    }}
                    components={{ strong: <strong /> }}
                  />
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </FormControl>
    </StyledBox>
  );
}
